// Canonical button primitive — ported from the TB-EMS SPA so the two apps
// read as siblings inside ThingsBoard.
//
// Three variants cover every button we actually render:
//   - `outlined` — neutral outlined button matching TB's own chrome. The
//     default; used for secondary CTAs (back-links, refresh, download).
//   - `accent`   — primary CTA filled with `--ems-brand` (the electric-blue
//     identity accent). Used sparingly — "Save", "Apply", "Confirm".
//   - `subtle-{tone}` — outlined button with a low-saturation semantic
//     accent (ring + text + faint hover fill).
//
// Unlike TB-EMS's version, the tone class strings are backed by the
// operator-spa's theme-aware `--ems-*` token aliases (not a hardcoded
// slate palette) so every variant reads correctly in dark mode.

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export type SubtleTone = "slate" | "red" | "emerald" | "indigo" | "blue" | "amber";
export type ButtonVariant = "outlined" | "accent" | `subtle-${SubtleTone}`;

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  /** Optional leading icon / glyph, rendered before the label. */
  leading?: ReactNode;
  /** Optional trailing icon / arrow, rendered after the label. */
  trailing?: ReactNode;
  /** Size preset. "md" matches TB's default button height. */
  size?: "sm" | "md";
};

const SIZE_CLASS: Record<NonNullable<Props["size"]>, string> = {
  sm: "text-xs px-2.5 py-1 gap-1",
  md: "text-sm px-3 py-1.5 gap-1.5",
};

const SUBTLE_CLASS: Record<SubtleTone, string> = {
  slate:
    "border border-edge bg-surface text-text-muted hover:bg-surface-2 hover:text-text hover:border-edge-strong",
  red: "border border-critical/30 bg-critical/5 text-critical hover:bg-critical/10 hover:border-critical/50",
  // "emerald" is a legacy tone NAME — it is bound to the brand `accent`, which
  // is now the electric-blue identity (not green). Kept for API stability.
  emerald:
    "border border-accent/30 bg-accent/5 text-accent hover:bg-accent/10 hover:border-accent/50",
  indigo:
    "border border-charge/30 bg-charge/5 text-charge hover:bg-charge/10 hover:border-charge/50",
  blue: "border border-charge/30 bg-charge/5 text-charge hover:bg-charge/10 hover:border-charge/50",
  amber: "border border-warn/30 bg-warn/5 text-warn hover:bg-warn/10 hover:border-warn/50",
};

const OUTLINED_CLASS =
  "border border-edge bg-surface text-text-muted hover:bg-surface-2 hover:text-text hover:border-edge-strong";

const ACCENT_CLASS =
  "border border-transparent text-white [background:var(--ems-brand)] hover:[filter:brightness(1.06)]";

const BASE_CLASS =
  "inline-flex items-center justify-center font-semibold rounded-md transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed";

function classForVariant(variant: ButtonVariant): string {
  if (variant === "outlined") return OUTLINED_CLASS;
  if (variant === "accent") return ACCENT_CLASS;
  return SUBTLE_CLASS[variant.slice("subtle-".length) as SubtleTone];
}

export function Button({
  variant = "outlined",
  leading,
  trailing,
  size = "md",
  className,
  children,
  ...rest
}: Props) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(BASE_CLASS, SIZE_CLASS[size], classForVariant(variant), className)}
    >
      {leading && <span aria-hidden>{leading}</span>}
      <span>{children}</span>
      {trailing && <span aria-hidden>{trailing}</span>}
    </button>
  );
}
